<script setup lang="ts">
import { computed } from "vue";
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import QuizEngine from "~/features/computer/components/QuizEngine.vue";
import { computerMcqChapterByUnit, pythonMcqTopicKeys, pythonMcqTopicGroupByKey } from "~/features/computer/data/mcq-registry";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const pythonChapter = computerMcqChapterByUnit.python;
const topicOrder = pythonMcqTopicKeys;
const topicInsertAfter = computed(() => {
  let total = 0;

  for (const topic of topicOrder) {
    total += pythonMcqTopicGroupByKey[topic].questions.length;

    if (total >= 10) {
      return topic;
    }
  }

  return topicOrder[0];
});

function scrollToTopic(topic: string | "all") {
  if (topic === "all") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  document.getElementById(topic)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

usePageSeo({
  title: pythonChapter.seo.title,
  description: pythonChapter.seo.description,
  keywords: pythonChapter.seo.keywords,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Python", item: "/11th/computer/python/" },
    { name: "MCQs", item: "/11th/computer/python/mcqs/" },
  ],
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">11th Class Python MCQs (Chapter 2)</h1>
        <p class="text-sm leading-7 text-slate-200">Topic-wise MCQs with answers + explanations</p>

        <div class="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button type="button" class="snap-start shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900" @click="scrollToTopic('all')">
            All
          </button>
          <button
            v-for="topic in topicOrder"
            :key="topic"
            type="button"
            class="snap-start shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900"
            @click="scrollToTopic(topic)"
          >
            {{ pythonMcqTopicGroupByKey[topic].label }}
          </button>
        </div>
      </section>

      <section
        v-for="topic in topicOrder"
        :id="topic"
        :key="topic"
        class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 scroll-mt-24"
      >
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900">{{ pythonMcqTopicGroupByKey[topic].label }} MCQs</h2>
          <p class="text-sm text-slate-600">
            {{ pythonMcqTopicGroupByKey[topic].description }}
          </p>
        </div>

        <QuizEngine :questions="pythonMcqTopicGroupByKey[topic].questions" />

        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p class="text-sm font-medium text-slate-900">
            {{ topic === "loops" ? "Loops weak hain?" : topic === "functions" ? "Functions revise karne hain?" : `${pythonMcqTopicGroupByKey[topic].label} aur samajhna hai?` }}
          </p>
          <NuxtLink
            :to="pythonMcqTopicGroupByKey[topic].conceptTo"
            class="mt-2 inline-flex text-sm font-medium text-sky-700 hover:text-sky-900"
          >
            {{ topic === "loops" ? "Loops practice ->" : topic === "functions" ? "Functions practice ->" : `${pythonMcqTopicGroupByKey[topic].label} practice ->` }}
          </NuxtLink>
        </div>

        <div class="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p class="text-sm font-medium text-slate-900">Weak area booster</p>
          <p class="mt-1 text-sm text-slate-600">Agar is section me dikkat aa rahi hai to pehle concept page kholo, phir dobara MCQs attempt karo.</p>
        </div>

        <InteractiveTopics
          v-if="topic === topicInsertAfter"
          eyebrow="Python Chapter 2"
          title="Python Topic Practice"
          intro="Agar MCQs me weak area mil raha hai to yeh topic pages khol kar concept strong karein, phir dobara practice karein."
          :cards="pythonInteractiveCards"
        />
      </section>
    </div>
  </PageContainer>
</template>
